import { Injectable } from '@angular/core';
import { ToastrService } from '@services/toaster.service';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from '@services/local-storage.service';
import { GlobalDataService } from '@services/global-data.service';
import { ComposerApiService } from '@services/composer-api.service';
import { FolderApiService } from '@services/folder-api.service';
import { ProductEnrichmentApiService } from '@services/product-enrichment-api.service';
import { GitOperationsApiService } from '@services/git-operations-api.service';
import { ScipComplianceApiService } from '@services/scip-compliance-api.service';
import { Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { ProductSearchDialogComponent } from '@dialogs/product-search-dialog/product-search-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class ComposerService {
  constructor(
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    private localStorageService: LocalStorageService,
    private folderApiService: FolderApiService,
    private productEnrichmentApiService: ProductEnrichmentApiService,
    private gitOperationsApiService: GitOperationsApiService,
    private composerApiService: ComposerApiService,
    private globalDataService: GlobalDataService,
    private scipComplianceApiService: ScipComplianceApiService
  ) {
  }

  hasUnsavedChanges = false;

  resumeImport(resumeBomImport) {
    this.globalDataService.setResumeImport(resumeBomImport);
    this.router.navigate(['/composer', 'importer', 'bom_import']);
  }

  getEnrichProduct(rid) {
    const data = {
      multiple: true,
      selectOption: false,
      type: 'enrich-search',
      action: 'enrich-product-search',
      rid
    };
    const dialogRef = this.dialog.open(ProductSearchDialogComponent, {
      data,
      panelClass: 'standard-dialog'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.productEnrichmentApiService.enrichProduct(rid, result).subscribe({
          next: () => {
            this.toastrService.show('Product enriched successfully', 3000);
          },
          error: () => {}
        });
      }
    });
  }

  openMattermap(rid) {
    this.globalDataService.setSelectedApp(undefined);
    this.globalDataService.setPpmSelectedItem(undefined);
    this.router.navigate(['mattermaps/overview/', rid]);
  }

  dataOperation(action: 'duplicate'|'copy'|'merge'|'deploy'|'fork', rid) {
    if (action === 'duplicate') action = 'copy';
    this.gitOperationsApiService.dataSetOperations(action, rid).subscribe((res: any) => {
      this.toastrService.show('Success', 1000);
      let { rid } = res;
      rid = rid.replace('#', '');
      this.router.navigate(['/composer/metadata/', rid]);
    });
  }
  pull(rid) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Publish to Makersite',
        text: null,
        cancel_text: 'Cancel',
        publish_text: 'Publish',
        type: 'Addpullrequest',
      },
      panelClass: 'pullrequest-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const obj = {
          key: rid,
          comment: result,
        };
        if (rid && obj.comment !== undefined) {
          this.gitOperationsApiService.dataSetOperations('pull', rid, obj).subscribe({
            next: () => {
              this.toastrService.show('Published successfully', 2000);
              this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate(['/composer/metadata/', rid]);
              });
            },
            error: () => {}
          });
        }
      }
    });
  }

  openInMcda(rid) {
    this.router.navigate(['mcda', rid]);
  }

  reCalculate(selectedTabName, rid) {
    const tab = selectedTabName;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: `Re-calculate ${tab}`,
        text: `Re-calculating will replace all existing ${tab}. Are you sure?`,
        cancel_text: 'No',
        delete_text: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      if (tab === 'Environmental Impacts') {
        this.composerApiService.reCalculateEI(rid).subscribe({
          next: () => {
            this.toastrService.show('Environmental Impacts recalculated successfully');
          },
          error: () => {}
        });
      } else if (tab === 'Exemptions') {
        this.scipComplianceApiService.reCalculateExemptions(rid).subscribe({
          next: () => {
            this.toastrService.show('Exemptions recalculated successfully');
          },
          error: () => {}
        });
      }
    });
  }

  deleteData(productName, rid) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Item?',
        text: `Would you like to delete the ${productName} dataset?`,
        cancel_text: 'Cancel',
        delete_text: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      this.composerApiService.deleteData(rid).subscribe((res) => {
        const trash = Object.values(res)[0];
        const trashIds = trash.trash_rids;
        if (trash.message === 'success') {
          this.toastrService.showWithAction('Deleted Successfully', 'Undo', 5000).afterDismissed().subscribe((restore) => {
            if (!restore.dismissedByAction) return;
            this.folderApiService.restoreItem(trashIds[0]).subscribe({
              next: () => {
                this.toastrService.show('Restored successfully.');
                const restoredRid = trashIds[0].replace('#', '');
                this.router.navigate(['composer/metadata/', restoredRid]);
              },
              error: () => {}
            });
          });

          this.router.navigate(['/dashboard']);
        }
      });
    });
  }

  convertNormalTextToUrl(url: string) {
    return url.trim().toLowerCase().replace(' ', '-');
  }

  /**
   *
   * @param type is Share for share dialog and Invite for invite dialog
   * @param event event is row data
   */
  shareInvite(type, selectedTabName, rid) {
    const userInfo = this.localStorageService.getItem('user_info');
    let selected;
    if (selectedTabName) {
      selected = this.convertNormalTextToUrl(selectedTabName);
    }

    const link = `/composer/${selected}/${rid}`;

    const shareData = {
      type: 'showInvite',
      value: type,
      data: {
        fr: userInfo.email,
        link,
        rid,
        usersLackingAccessList: '',
      },
    };

    this.dialog.open(InviteShareComponent, {
      data: shareData,
      panelClass: 'show-invite',
    });
  }
}
